import { FC } from 'react';
import { get, useFormContext } from 'react-hook-form';
import { PunchupFieldLabel } from '../../fields/PunchupFieldLabel';
import { PunchupCombobox, PunchupComboboxProps } from './PunchupCombobox';

/** The combobox's own presentation props, plus the field wiring. Picked from the source type so the
 *  two cannot drift. */
export type PunchupFormComboboxProps = Pick<
	PunchupComboboxProps,
	'id' | 'options' | 'placeholder' | 'searchPlaceholder' | 'disabled' | 'emptyLabel'
> & {
	/** Field path in the enclosing `<FormProvider>`. The control id defaults to it. */
	name: string;
	label?: string;
	/** Static "explain this field" copy, rendered as the label's help tooltip. */
	help?: string;
};

/**
 * `PunchupCombobox` bound to a `react-hook-form` field: it mirrors `PunchupFormSelect`'s contract
 * (watch / setValue / field error / dirty tint / label + help) and leaves every panel behaviour to
 * the combobox it composes. See `PunchupCombobox/CLAUDE.md` for when to reach for it over
 * `PunchupFormSelect`.
 */
export const PunchupFormCombobox: FC<PunchupFormComboboxProps> = ({
	id,
	name,
	label,
	options,
	placeholder,
	searchPlaceholder,
	help,
	disabled,
	emptyLabel,
}) => {
	const {
		watch,
		setValue,
		formState: { errors, dirtyFields },
	} = useFormContext();

	const controlId = id ?? name;
	const value = watch(name);
	const error = get(errors, name)?.message as string | undefined;
	// `get` (not a plain index) so a nested field path — `dates[0].timezone` — resolves too.
	const isDirty = !!get(dirtyFields, name);

	return (
		<div style={{ width: '100%' }}>
			{label && <PunchupFieldLabel label={label} htmlFor={controlId} help={help} />}
			<PunchupCombobox
				id={controlId}
				size="form"
				options={options}
				value={value ?? null}
				onChange={(next) => setValue(name, next, { shouldValidate: true, shouldDirty: true })}
				placeholder={placeholder}
				searchPlaceholder={searchPlaceholder}
				disabled={disabled}
				dirty={isDirty}
				error={error}
				emptyLabel={emptyLabel}
			/>
		</div>
	);
};
